type WaitOptions = {
  resolveSignal?: AbortSignal; //A signal that will abort the wait by resolving the wait promise
  rejectSignal?: AbortSignal; //A signal that will abort the wait by rejecting the wait promise
};

//Examples:
//```
//wait(); //defer one event loop before continuing
//wait(100); //wait 100ms
//```
export const wait = (
  delayMs?: number | null,
  options?: WaitOptions,
): Promise<void> => {
  const { resolveSignal, rejectSignal } = options ?? {};
  return new Promise((resolve, reject) => {
    let timeoutId: null | ReturnType<typeof setTimeout>;
    let rejectHandler: null | (() => void);
    let resolveHandler: null | (() => void);

    const cleanup = () => {
      timeoutId && clearTimeout(timeoutId);
      rejectHandler &&
        rejectSignal?.removeEventListener('abort', rejectHandler);
      resolveHandler &&
        resolveSignal?.removeEventListener('abort', resolveHandler);
      timeoutId = rejectHandler = resolveHandler = null;
    };

    resolveHandler = () => {
      if (resolveHandler) {
        resolve();
        cleanup();
      }
    };

    if (rejectSignal) {
      const rejectMessage = `Wait for ${delayMs} aborted because received reject AbortSignal`;
      if (rejectSignal.aborted) {
        //No need to wait
        reject(rejectMessage);
        return;
      } else {
        rejectHandler = () => {
          if (rejectHandler) {
            reject(rejectMessage);
            cleanup();
          }
        };
        rejectSignal.addEventListener('abort', rejectHandler);
      }
    }

    if (resolveSignal) {
      if (resolveSignal.aborted) {
        //No need to wait
        resolve();
        return;
      } else {
        resolveSignal.addEventListener('abort', resolveHandler);
      }
    }

    timeoutId = setTimeout(resolveHandler, delayMs || 0);
  });
};

//https://developer.mozilla.org/en-US/docs/Web/API/HTML_DOM_API/Microtask_guide
export const microWait = () => Promise.resolve();
